import React, { useState, useEffect, useRef } from 'react';

interface TabNavigationProps {
    tabs: string[];
    activeTab: string;
    onTabChange: (tab: string) => void;
    colors?: Record<string, string>;
    onAddTab?: () => void;
    onEditTab?: () => void;
    onSaveJson?: () => void;
    editable?: boolean;
    onEnableEdit?: (editable: boolean) => void;
    colouredText?: boolean;
}

const TabNavigation: React.FC<TabNavigationProps> = ({
    tabs,
    activeTab,
    onTabChange,
    colors = {},
    onAddTab,
    onEditTab,
    onSaveJson,
    editable = true,
    onEnableEdit,
    colouredText = false,
}) => {
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });
    const tabsRef = useRef<{ [key: string]: HTMLButtonElement | null }>({});
    const containerRef = useRef<HTMLDivElement>(null);

    // Get active color or fallback to a standard blue
    const activeColor = colors[activeTab] || '#3b82f6';

    // Effect for updating the indicator and auto-centering selected tab
    useEffect(() => {
        const updateIndicator = () => {
            const currentTab = tabsRef.current[activeTab];
            const container = containerRef.current;

            if (currentTab && container) {
                // 1. Calculate Lamp Position
                const left = currentTab.offsetLeft;
                const width = currentTab.offsetWidth;

                setIndicatorStyle({
                    left,
                    width,
                    opacity: 1,
                });

                // 2. Auto-scroll container to center the active tab
                const containerWidth = container.offsetWidth;
                const scrollLeft = left + width / 2 - containerWidth / 2;

                // We keep smooth behavior here programmatically
                container.scrollTo({
                    left: scrollLeft,
                    behavior: 'smooth',
                });
            }
        };

        updateIndicator();
        window.addEventListener('resize', updateIndicator);
        const timeoutId = setTimeout(updateIndicator, 100);

        return () => {
            window.removeEventListener('resize', updateIndicator);
            clearTimeout(timeoutId);
        };
    }, [activeTab, tabs]);

    // Effect for Mouse Wheel (Desktop Horizontal Scroll)
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheel = (e: WheelEvent) => {
            if (e.deltaY === 0) return;
            if (e.shiftKey) return;
            container.scrollLeft += e.deltaY;
            e.preventDefault();
        };

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, []);

    return (
        <div className="w-full mb-8 flex justify-start">
            <div className="relative p-1.5 bg-gray-200/50 dark:bg-slate-800/50 rounded-xl shadow-inner w-full flex items-center">
                {/* Scrollable Container for Tabs */}
                <div
                    ref={containerRef}
                    className="flex relative overflow-x-auto no-scrollbar items-center flex-1 touch-pan-x rounded-lg"
                >
                    {/* The "Lava Lamp" Indicator */}
                    <div
                        className="absolute top-0 bottom-0 my-auto h-full rounded-lg transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] pointer-events-none"
                        style={{
                            left: `${indicatorStyle.left}px`,
                            width: `${indicatorStyle.width}px`,
                            opacity: indicatorStyle.opacity,
                            backgroundColor: activeColor,
                            boxShadow: `0 2px 20px -2px ${activeColor}80`,
                        }}
                    />

                    {/* Tab Buttons */}
                    {tabs.map((tab) => {
                        const isActive = tab === activeTab;
                        const tabTextColor = colouredText ? colors[tab] : undefined;
                        return (
                            <div className="flex">
                                <button
                                    key={tab}
                                    ref={(el) => {
                                        if (el) tabsRef.current[tab] = el;
                                    }}
                                    onClick={() => onTabChange(tab)}
                                    style={{
                                        color: isActive ? '#ffffff' : tabTextColor ? tabTextColor : undefined,
                                        textShadow: isActive ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
                                    }}
                                    className={`
                    relative z-10 px-5 py-2.5 text-sm font-bold transition-colors duration-300 rounded-lg whitespace-nowrap capitalize tracking-wide
                    ${!isActive ? (tabTextColor ? '' : 'text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200') : ''}
                `}
                                >
                                    {tab}
                                </button>
                                {isActive && onEditTab && (
                                    <div className="flex-shrink-0 px-2 ml-1 border-r border-gray-300 dark:border-slate-700 flex items-center">
                                        <button
                                            onClick={onEditTab}
                                            className="p-2 text-gray-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors rounded-lg hover:bg-white dark:hover:bg-slate-700/50"
                                            title="Edit tab settings"
                                        >
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Add Tab Button */}
                    {onAddTab && (
                        <button
                            onClick={onAddTab}
                            className="relative z-10 ml-2 px-3 py-1.5 rounded-lg border-2 border-dashed border-gray-400 dark:border-slate-500 text-gray-400 dark:text-slate-500 hover:border-blue-500 hover:text-blue-500 dark:hover:border-blue-400 dark:hover:text-blue-400 transition-colors flex items-center justify-center shrink-0"
                            title="Add new tab"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Edit Options Button */}
                {!onEditTab && editable && (
                    <div className="flex-shrink-0 pl-2 ml-1 border-l border-gray-300 dark:border-slate-700 flex items-center">
                        <button
                            onClick={() => {
                                if (onEnableEdit) onEnableEdit(true);
                            }}
                            className="p-2 text-gray-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors rounded-lg hover:bg-white dark:hover:bg-slate-700/50"
                            title="Edit mode"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125"
                                />
                            </svg>
                        </button>
                    </div>
                )}

                {/* Save JSON Button */}
                {onSaveJson && (
                    <div className="flex-shrink-0 pl-2 ml-1 border-l border-gray-300 dark:border-slate-700 flex items-center">
                        <button
                            onClick={onSaveJson}
                            className="p-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 transition-colors rounded-lg hover:bg-white dark:hover:bg-slate-700/50 flex items-center gap-1.5"
                            title="Save JSON"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2.12"
                            >
                                <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
                                <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
                                <path d="M7 3v4a1 1 0 0 0 1 1h7" />
                            </svg>
                            <span className="text-xs font-bold hidden sm:inline">Save</span>
                        </button>
                        <button
                            onClick={() => {
                                if (onEnableEdit) onEnableEdit(false);
                            }}
                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-white dark:hover:bg-slate-700/50 flex items-center gap-1.5"
                            title="Cancel edit"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2.12"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <path d="m15 9-6 6" />
                                <path d="m9 9 6 6" />
                            </svg>
                            <span className="text-xs font-bold hidden sm:inline">Cancel</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TabNavigation;
